// ============================================================
// Commandery Secure Portal · /commandery/secure-portal/access + /session
//
// The MEMBER-facing secure access surface. Adapts the two approved design
// contracts into real, wired states:
//   - commandery-landing-v1-0-0-2026-07-01.html   → access-entry (idle)
//   - commandery-verifying-v1-0-0-2026-07-01.html → verifying + terminal states
//
// The card content (titles, copy, fields, loader, footer) is real HTML/CSS — no
// state text is baked into the artwork. The loading bar + dots animate (the
// design's @keyframes are preserved verbatim). The full-scene artwork + seal are
// the design's own images, served as static assets.
//
// Behavior is driven by useCommanderyAccess → Square Code redeem/verify-session
// proxies → Olivium Secure Share. Olivium is the trust layer ("Protected by
// Olivium"). No secrets are ever rendered; the access code never touches the URL,
// storage, logs, or receipts. Fails closed to `not_live` until the portal gate
// is enabled server-side.
// ============================================================

import { useEffect, useState } from 'react'

import { useCommanderyAccess } from './useCommanderyAccess'
import type { CommanderyAccessState } from './commanderyAccess'

import sceneLanding from './assets/commandery-scene-landing.jpg'
import sceneVerifying from './assets/commandery-scene-verifying.jpg'
import sealImg from './assets/commandery-seal.jpg'

// Terminal states that a member can retry from by re-entering their code.
const RETRYABLE = new Set<CommanderyAccessState>([
  'denied', 'expired', 'revoked', 'already_used', 'no_session', 'error',
])

export default function SecurePortal({ mode = 'access' }: { mode?: 'access' | 'session' }) {
  const { state, stateInfo, invitePrefill, busy, accountState, submit, reset } = useCommanderyAccess({ mode })

  const [inviteId, setInviteId] = useState('')
  const [code, setCode] = useState('')

  // Sync the invite prefill (id only) once, without clobbering member typing.
  useEffect(() => {
    if (invitePrefill) setInviteId((cur) => (cur ? cur : invitePrefill))
  }, [invitePrefill])

  function onSubmit() {
    if (busy || !inviteId.trim() || !code.trim()) return
    const c = code
    // Clear the code from component state as soon as it is handed to the proxy —
    // it never persists anywhere on the client.
    setCode('')
    void submit({ inviteId: inviteId.trim(), code: c })
  }

  const showEntry = state === 'idle' || state === 'submitting'
  const showVerifying = state === 'verifying'
  const scene = showEntry ? sceneLanding : sceneVerifying

  return (
    <div className="cmdy">
      <style dangerouslySetInnerHTML={{ __html: PORTAL_CSS }} />

      <div className="scene">
        <img src={scene} alt="West L. Coleman Commandery No. 4 secure access" />
      </div>

      <div className="stagewrap">
        {showEntry && (
          <EntryCard
            inviteId={inviteId}
            code={code}
            invitePrefilled={Boolean(invitePrefill) && inviteId === invitePrefill}
            busy={busy}
            onInvite={setInviteId}
            onCode={setCode}
            onSubmit={onSubmit}
          />
        )}
        {showVerifying && <VerifyingCard title={stateInfo.title} message={stateInfo.message} />}
        {!showEntry && !showVerifying && (
          <ResultCard
            code={state}
            title={stateInfo.title}
            message={stateInfo.message}
            accountState={accountState}
            canRetry={mode === 'access' && RETRYABLE.has(state)}
            onRetry={reset}
          />
        )}
      </div>

      <div className="buildtag">West L. Coleman Commandery No. 4 · secure access · v1.0.0</div>
    </div>
  )
}

// ── Access entry (landing design) ──
function EntryCard(props: {
  inviteId: string; code: string; invitePrefilled: boolean; busy: boolean
  onInvite: (v: string) => void; onCode: (v: string) => void; onSubmit: () => void
}) {
  const canSubmit = !props.busy && props.inviteId.trim().length > 0 && props.code.trim().length > 0
  return (
    <div className="lcard">
      <div className="lseal"><img className="lseal-img" src={sealImg} alt="West L. Coleman Commandery No. 4 seal" /></div>

      <div className="lpane on">
        <div className="lfield">
          <label htmlFor="cmdy-invite">Invite reference</label>
          <input
            id="cmdy-invite" value={props.inviteId} autoComplete="off" spellCheck={false}
            placeholder="INVITE-REFERENCE"
            onChange={(e) => props.onInvite(e.target.value)}
          />
          {props.invitePrefilled && <p className="lhint">Prefilled from your secure link.</p>}
        </div>
        <div className="lfield">
          <label htmlFor="cmdy-code">One-time access code</label>
          <input
            id="cmdy-code" value={props.code} autoComplete="off" spellCheck={false}
            placeholder="OLV-XXXX-XXXX-XXXX"
            onChange={(e) => props.onCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') props.onSubmit() }}
          />
        </div>
        <button className="btn gold" type="button" disabled={!canSubmit} onClick={props.onSubmit}>
          Continue with access key
        </button>
      </div>

      <p className="laccess">Membership is confirmed by Commandery leadership. Approved members and invited candidates only.</p>
      <div className="ldiv" />
      <p className="lfoot">Protected by <span className="ol">Olivium</span> · Secure member workspace</p>
    </div>
  )
}

// ── Verifying (verifying design — animated) ──
function VerifyingCard({ title, message }: { title: string; message: string }) {
  return (
    <div className="vcard">
      <Shield />
      <div className="vseal"><img src={sealImg} alt="West L. Coleman Commandery No. 4 seal" /></div>
      <h1 className="vtitle">{title}</h1>
      <div className="vdiv"><span className="line" /><span className="dia" /><span className="line r" /></div>
      <p className="vsub">{message}</p>
      <div className="vload">
        <div className="vtrack" />
        <div className="vdotsrow"><i /><i /><i /><i /><i className="last" /></div>
      </div>
      <Cross />
    </div>
  )
}

// ── Terminal result (reuses the verifying frame; static, no loader) ──
function ResultCard(props: {
  code: CommanderyAccessState; title: string; message: string
  accountState: CommanderyAccessState | null; canRetry: boolean; onRetry: () => void
}) {
  const verified = props.code === 'verified'
  return (
    <div className="vcard">
      <Shield />
      <div className="vseal"><img src={sealImg} alt="West L. Coleman Commandery No. 4 seal" /></div>
      <h1 className="vtitle">{props.title}</h1>
      <div className="vdiv"><span className="line" /><span className="dia" /><span className="line r" /></div>
      <p className="vsub">{props.message}</p>
      {verified && props.accountState === 'account_bootstrap_not_implemented' && (
        <p className="vnote">Member account setup isn’t available yet — this is a secure session only.</p>
      )}
      {props.canRetry && (
        <button className="btn gold btn-inline" type="button" onClick={props.onRetry}>Try again</button>
      )}
      <Cross />
    </div>
  )
}

function Shield() {
  return (
    <svg className="vshield" viewBox="0 0 24 24" fill="none" stroke="var(--goldB)" strokeWidth="1.4" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2.4l7.2 2.6v5.4c0 4.6-2.9 7.6-7.2 8.4-4.3-.8-7.2-3.8-7.2-8.4V5z" fill="rgba(20,7,7,.9)" />
      <path d="M12 6.5v9M8 9h8" stroke="var(--accentB)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}
function Cross() {
  return (
    <svg className="vcross" viewBox="0 0 24 24" fill="none" stroke="var(--goldB)" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M12 2.5v19M4.5 8.5h15" strokeWidth="2.2" />
    </svg>
  )
}

// Adapted verbatim from the two approved design contracts, scoped under `.cmdy`
// so nothing leaks into the operator shell. The loader keyframes are preserved.
const PORTAL_CSS = `
.cmdy, .cmdy *{box-sizing:border-box}
.cmdy{
  position:fixed;inset:0;z-index:0;overflow:auto;background:#000;
  --gold:#D4AF37;--goldB:#E6B84F;--accent:#B0182A;--accentB:#E2434F;
  --ink:#F3EFEA;--dim:#B9AFA4;--muted:#7c7268;
  --ua:#E63946;--gold2:#FF8B8B;--olivium:#A78BFA;
  --ink2:rgba(246,242,240,.7);--ink3:rgba(246,242,240,.45);
  --bd:color-mix(in srgb,var(--ua) 16%,transparent);--bd2:color-mix(in srgb,var(--ua) 30%,transparent);--bd3:color-mix(in srgb,var(--ua) 50%,transparent);
  --serif:'Cormorant Garamond',Georgia,serif;--sans:'Outfit',system-ui,sans-serif;--mono:'JetBrains Mono',ui-monospace,monospace;
  font-family:var(--sans);color:var(--ink);-webkit-font-smoothing:antialiased;
}
.cmdy ::selection{background:rgba(212,175,55,.28)}
.cmdy :focus-visible{outline:2px solid var(--gold2);outline-offset:2px;border-radius:6px}
.cmdy input{font-family:inherit}
.cmdy button{font-family:inherit;cursor:pointer;border:none;background:none;color:inherit}
.cmdy button:disabled{opacity:.55;cursor:not-allowed}

.cmdy .scene{position:fixed;inset:0;z-index:0;overflow:hidden;background:#000}
.cmdy .scene img{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:100%;height:100%;object-fit:contain;display:block}

.cmdy .stagewrap{position:fixed;inset:0;z-index:1;display:flex;align-items:center;justify-content:center;pointer-events:none}

/* ---- access-entry card (landing) ---- */
.cmdy .lcard{
  position:relative;pointer-events:auto;top:2%;
  width:clamp(280px,32vw,430px);max-height:min(80vh,660px);overflow-y:auto;overflow-x:hidden;
  background:rgba(9,6,7,.5);-webkit-backdrop-filter:blur(22px) saturate(1.2);backdrop-filter:blur(22px) saturate(1.2);
  border:1px solid rgba(255,255,255,.1);border-radius:20px;
  padding:clamp(18px,2.4vw,28px) clamp(18px,2.4vw,28px) clamp(14px,1.8vw,20px);
  box-shadow:0 50px 120px -40px #000,inset 0 1px 0 rgba(255,255,255,.07);text-align:center;
}
.cmdy .lseal{position:relative;width:clamp(78px,9vw,116px);height:clamp(78px,9vw,116px);margin:0 auto clamp(10px,1.4vw,16px);border-radius:50%;display:grid;place-items:center;overflow:hidden;background:radial-gradient(circle,rgba(14,8,9,.85),rgba(0,0,0,.6));box-shadow:0 22px 54px -18px rgba(0,0,0,.72),0 0 40px -10px rgba(207,28,39,.4)}
.cmdy .lseal-img{width:100%;height:100%;border-radius:50%;object-fit:cover;display:block}
.cmdy .lpane{text-align:left}
.cmdy .lfield{text-align:left;margin-bottom:11px}
.cmdy .lfield label{font-family:var(--mono);font-size:9.5px;letter-spacing:.06em;text-transform:uppercase;color:var(--ink3);display:block;margin-bottom:6px}
.cmdy .lfield input{width:100%;background:rgba(8,8,8,.7);border:1px solid var(--bd2);border-radius:10px;padding:11px 12px;color:var(--ink);font-size:13.5px;outline:none;transition:.14s}
.cmdy .lfield input:focus{border-color:var(--bd3)}
.cmdy .lhint{font-family:var(--mono);font-size:9px;color:var(--ink3);margin-top:6px}
.cmdy .btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;font-size:13.5px;font-weight:600;padding:11px 16px;border-radius:11px;transition:.16s;white-space:nowrap;width:100%}
.cmdy .btn.gold{color:#fff;background:linear-gradient(135deg,var(--ua),color-mix(in srgb,var(--ua) 60%,#000));box-shadow:0 14px 30px -14px color-mix(in srgb,var(--ua) 65%,transparent)}
.cmdy .btn.gold:hover:not(:disabled){filter:brightness(1.07)}
.cmdy .btn-inline{width:auto;margin-bottom:clamp(12px,2vh,20px)}
.cmdy .laccess{font-size:11px;color:var(--ink3);margin-top:14px;line-height:1.5}
.cmdy .ldiv{height:1px;background:var(--bd);margin:14px 0 11px}
.cmdy .lfoot{font-size:10.5px;color:var(--ink3)}
.cmdy .lfoot .ol{color:var(--olivium);font-weight:600}

/* ---- verifying / result card ---- */
.cmdy .vcard{position:relative;top:2.4%;width:clamp(320px,42vw,560px);max-height:min(62vh,480px);display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:clamp(10px,1.6vh,20px) clamp(20px,3vw,34px) clamp(10px,1.4vh,16px);text-align:center;overflow:hidden;pointer-events:auto}
.cmdy .vshield{width:clamp(24px,2.6vw,32px);height:clamp(24px,2.6vw,32px);margin-bottom:clamp(10px,1.6vh,16px);flex:0 0 auto}
.cmdy .vseal{width:clamp(70px,8vw,100px);height:clamp(70px,8vw,100px);margin-bottom:clamp(10px,1.4vh,14px);position:relative;flex:0 0 auto}
.cmdy .vseal img{width:100%;height:100%;border-radius:50%;object-fit:cover;display:block;box-shadow:0 0 0 1px rgba(212,175,55,.35),0 10px 30px rgba(0,0,0,.5)}
.cmdy .vtitle{font-family:var(--serif);font-weight:600;font-size:clamp(19px,2.6vw,30px);letter-spacing:.2px;background:linear-gradient(180deg,#F3D98A,var(--gold) 60%,var(--goldB));-webkit-background-clip:text;background-clip:text;color:transparent;margin-bottom:clamp(8px,1.2vh,12px);line-height:1.15}
.cmdy .vdiv{display:flex;align-items:center;gap:10px;width:min(100%,300px);margin-bottom:clamp(10px,1.4vh,16px);flex:0 0 auto}
.cmdy .vdiv .line{flex:1;height:1px;background:linear-gradient(90deg,transparent,rgba(176,24,42,.6))}
.cmdy .vdiv .line.r{background:linear-gradient(90deg,rgba(176,24,42,.6),transparent)}
.cmdy .vdiv .dia{width:6px;height:6px;background:var(--accentB);transform:rotate(45deg);flex:0 0 auto;box-shadow:0 0 8px rgba(226,67,79,.6)}
.cmdy .vsub{font-size:clamp(12px,1.15vw,14.5px);line-height:1.55;color:var(--dim);max-width:420px;margin-bottom:clamp(14px,2.2vh,26px);flex:0 0 auto}
.cmdy .vnote{font-size:11.5px;line-height:1.5;color:var(--ink3);max-width:400px;margin-bottom:clamp(12px,2vh,20px)}
.cmdy .vload{width:100%;max-width:380px;display:flex;align-items:center;gap:16px;margin-bottom:clamp(12px,2vh,22px);flex:0 0 auto}
.cmdy .vtrack{position:relative;flex:1;height:2px;background:rgba(255,255,255,.08);border-radius:2px;overflow:hidden}
.cmdy .vtrack::after{content:'';position:absolute;top:0;bottom:0;left:-40%;width:40%;border-radius:2px;background:linear-gradient(90deg,transparent,var(--goldB) 45%,#FFE9B8 50%,var(--goldB) 55%,transparent);filter:drop-shadow(0 0 5px rgba(230,184,79,.7));animation:cmdy-vsweep 2s cubic-bezier(.4,0,.2,1) infinite}
@keyframes cmdy-vsweep{0%{left:-40%}100%{left:100%}}
.cmdy .vdotsrow{display:flex;align-items:center;gap:6px;flex:0 0 auto}
.cmdy .vdotsrow i{width:5px;height:5px;border-radius:50%;background:rgba(255,255,255,.18);display:block;animation:cmdy-vstep 2s ease-in-out infinite}
.cmdy .vdotsrow i:nth-child(1){animation-delay:0s}
.cmdy .vdotsrow i:nth-child(2){animation-delay:.15s}
.cmdy .vdotsrow i:nth-child(3){animation-delay:.3s}
.cmdy .vdotsrow i:nth-child(4){animation-delay:.45s}
.cmdy .vdotsrow i.last{background:var(--accentB);box-shadow:0 0 8px rgba(226,67,79,.7);animation:cmdy-vstepred 2s ease-in-out infinite}
@keyframes cmdy-vstep{0%,100%{background:rgba(255,255,255,.18);transform:scale(1)}15%{background:var(--goldB);transform:scale(1.35)}35%{background:rgba(255,255,255,.18);transform:scale(1)}}
@keyframes cmdy-vstepred{0%,80%{background:rgba(226,67,79,.35);box-shadow:none;transform:scale(1)}95%{background:var(--accentB);box-shadow:0 0 8px rgba(226,67,79,.7);transform:scale(1.4)}100%{background:rgba(226,67,79,.35);box-shadow:none;transform:scale(1)}}
.cmdy .vcross{width:clamp(18px,2vw,24px);height:clamp(18px,2vw,24px);flex:0 0 auto;opacity:.9}

.cmdy .buildtag{position:fixed;right:12px;bottom:10px;font-family:var(--mono);font-size:9.5px;color:rgba(243,239,234,.28);z-index:5;pointer-events:none;background:rgba(5,3,3,.5);padding:3px 7px;border-radius:6px}

@media(max-width:640px){
  .cmdy .scene img{object-fit:cover;object-position:50% 40%;filter:blur(18px) brightness(.48) saturate(1.15);transform:translate(-50%,-50%) scale(1.08)}
  .cmdy .stagewrap{padding:22px 16px}
  .cmdy .lcard,.cmdy .vcard{top:0;width:100%;max-width:400px;max-height:none;background:rgba(8,5,4,.74);-webkit-backdrop-filter:blur(16px) saturate(1.1);backdrop-filter:blur(16px) saturate(1.1);border:1px solid rgba(212,175,55,.32);border-radius:20px;box-shadow:0 30px 80px -20px rgba(0,0,0,.7),inset 0 1px 0 rgba(255,255,255,.06);padding:26px 22px 22px}
}
@media(prefers-reduced-motion:reduce){
  .cmdy .vtrack::after,.cmdy .vdotsrow i,.cmdy .vdotsrow i.last{animation:none}
}
`
